import { Button, Textarea } from "@mantine/core";
import {
  IconAlertTriangle,
  IconBuildingStore,
  IconCalendar,
  IconCash,
  IconCircleCheck,
  IconClipboardCheck,
  IconCreditCard,
  IconFileText,
  IconHash,
  IconMapPin,
  IconNotes,
  IconPackage,
  IconPaperclip,
  IconPencil,
  IconPrinter,
  IconReceipt,
  IconShoppingCart,
  IconTruck,
  IconUser,
} from "@tabler/icons-react";
import { useEffect, useState } from "react";

import { authClient } from "@/lib/auth-clients";
import dayjs from "dayjs";
import { formatDeliveryLocation } from "@/lib/purchaseOrder";
import { message } from "antd";
import PurchaseOrderPrintSheet, {
  type PurchaseOrderPrintData,
  purchaseOrderPrintHtml,
} from "@/components/PurchaseOrderPrintSheet";
import SignPrintPreview from "@/components/SignPrintPreview";
import { orpcClient } from "@/lib/orpc-client";
import { single } from "@/lib/orpc-batch";
import { useData } from "@/utility/state/contextAPI";
import { useNavigate } from "react-router-dom";
import { useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";

const PRINT_PREVIEW_MARGIN = { topMm: 0, sideMm: 0 };

function PurchaseOrderPage() {
  const { t } = useTranslation();
  const [rejectReason, setRejectReason] = useState("");
  const [purchaseOrderData, setPurchaseOrderData] = useState({
    name: "",
    partnerId: "",
    purchaseDate: "",
    deliveryDate: "",
    status: "draft",
    requiresInvoice: false,
    requiresIncomingQualityControl: false,
    shippingFeeCalculation: "freeShipping",
    company: localStorage.getItem("companyId"),
    deliveryMethod: "single",
    paymentMethod: "",
    deliveryLocation: "",
    remark: "",
    notice: "",
    actualShippingFee: "",
    orderReminderDate: "",
    deliveryReceiver: "",
    applicantId: "",
    applicationDate: "",
    reviewDate: "",
    attachment: [],
    reviewerId: "",
    rejectedReason: "",
    purchaseOrderProduct: [] as any[],
  });
  const [productData, setProductData] = useState<any[]>([]);
  const [companyData, setCompanyData] = useState<any>(null);

  const [partnerData, setPartnerData] = useState<any[]>([]);
  const [isRejected, setIsRejected] = useState(false);
  const [employeeData, setEmployeeData] = useState<any[]>([]);
  const [userRoles, setUserRoles] = useState<string[]>([]);
  const [currentEmployeeId, setCurrentEmployeeId] = useState<string | null>(
    null
  );
  const [submitting, setSubmitting] = useState(false);
  const {
    modal: { contextOpenModal, contextCloseModal, contextSetModalContent },
  } = useData();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    shippingFeeCalculation,
    deliveryMethod,
    requiresInvoice,
    requiresIncomingQualityControl,
    partnerId,
    purchaseDate,
    deliveryDate,
    status: orderStatus,
    paymentMethod,
    deliveryLocation,
    remark,
    notice,
    actualShippingFee,
    orderReminderDate,
    deliveryReceiver,
    name,
    applicantId,
    attachment,
    applicationDate,
    reviewDate,
    reviewerId,
    rejectedReason,
    purchaseOrderProduct,
  } = purchaseOrderData || {};

  const canApprove =
    userRoles.includes("owner") ||
    userRoles.includes("admin") ||
    userRoles.includes("purchaseManager");

  const selectedPartner = partnerData?.find((p) => p?.id === partnerId);

  // 審核完成（已審核／已完成）才開放列印、傳真或存成 PDF
  const canPrint = orderStatus === "approved" || orderStatus === "completed";

  const [printHtml, setPrintHtml] = useState<string | null>(null);

  const handlePrint = () => {
    setPrintHtml(
      purchaseOrderPrintHtml(
        printData,
        name || t("purchaseOrder.printTitle") || "採購單",
      ),
    );
  };

  const fetchOrder = async () => {
    try {
      let result = await single(orpcClient.purchaseOrder.read([{ id: id! }]));
      if (!result) return;
      setPurchaseOrderData(result);
    } catch (error) {
      console.error("Error fetching purchaseOrder data:", error);
    }
  };

  const handleRunAction = async (action, successMessage, failMessage) => {
    if (!id || submitting) return;
    setSubmitting(true);
    try {
      await action();
      message.success(successMessage);
      setIsRejected(false);
      setRejectReason("");
      await fetchOrder();
    } catch (error) {
      console.error("purchaseOrder review action error:", error);
      message.error(error?.message || failMessage);
    } finally {
      setSubmitting(false);
    }
  };

  const handleRequestApproval = () =>
    handleRunAction(
      () => single(orpcClient.purchaseOrder.requestApproval([{ id: id! }])),
      t("purchaseOrder.submitSuccess") || "採購單提交審核成功",
      t("purchaseOrder.submitFailed") || "採購單送審失敗"
    );

  const handleApprove = () =>
    handleRunAction(
      () => single(orpcClient.purchaseOrder.approve([{ id: id! }])),
      t("purchaseOrder.approveSuccess") || "採購單審核通過",
      t("purchaseOrder.approveFailed") || "採購單審核失敗"
    );

  const handleReject = () => {
    if (!rejectReason.trim()) {
      message.error(
        t("purchaseOrder.rejectReasonRequired") || "請填寫審核不通過原因"
      );
      return;
    }

    return handleRunAction(
      () =>
        single(
          orpcClient.purchaseOrder.reject([
            { id: id!, rejectedReason: rejectReason.trim() },
          ])
        ),
      t("purchaseOrder.rejectSuccess") || "採購單已駁回",
      t("purchaseOrder.rejectFailed") || "採購單駁回失敗"
    );
  };

  const handleComplete = () =>
    handleRunAction(
      () => single(orpcClient.purchaseOrder.complete([{ id: id! }])),
      t("purchaseOrder.completeSuccess") || "採購單已完成",
      t("purchaseOrder.completeFailed") || "採購單完成失敗"
    );

  const handleDisplayEmployeeName = (employeeId) =>
    employeeData?.find((e) => e?.id === employeeId)?.name || "";

  const handleDisplayDate = (value, fallback = "-") =>
    value ? dayjs(value).format("YYYY-MM-DD") : fallback;

  const pendingSubmitText =
    t("purchaseOrder.pendingAfterSubmit") || "送審後產生";
  const pendingReviewText =
    t("purchaseOrder.pendingAfterReview") || "審核後產生";

  const handleDisplayApplicant = () => {
    if (applicantId)
      return handleDisplayEmployeeName(applicantId) || applicantId;

    const currentEmployeeName = handleDisplayEmployeeName(currentEmployeeId);
    if (orderStatus === "draft" && currentEmployeeName)
      return `${currentEmployeeName}${
        t("purchaseOrder.applicantPending") || "（待送審）"
      }`;

    return pendingSubmitText;
  };

  const handleDisplayAttachment = (value) => {
    if (!value) return [];
    if (Array.isArray(value)) return value;

    try {
      const parsed = JSON.parse(value);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  };

  const handleDisplayStatusBadge = (status) => {
    const palette = {
      draft: "bg-gray-100 text-gray-600",
      underReview: "bg-amber-100 text-amber-700",
      approved: "bg-blue-100 text-blue-700",
      completed: "bg-emerald-100 text-emerald-700",
      rejected: "bg-red-100 text-red-700",
    };
    const label = {
      draft: t("purchaseOrder.draft") || "草稿",
      underReview: t("purchaseOrder.underReview") || "審核中",
      approved: t("purchaseOrder.approved") || "已審核",
      completed: t("purchaseOrder.completed") || "已完成",
      rejected: t("purchaseOrder.rejected") || "已駁回",
    };

    if (!status) return null;

    return (
      <span
        className={`rounded-full px-3 py-1 text-xs font-medium ${
          palette[status] || palette.draft
        }`}
      >
        {label[status] || status}
      </span>
    );
  };

  const renderField = (
    label: string,
    value: React.ReactNode,
    Icon?: React.ComponentType<{ size?: number | string; className?: string }>
  ) => (
    <div className="space-y-1.5">
      <label className="flex items-center gap-1.5 text-xs text-gray-500">
        {Icon ? <Icon size={13} /> : null}
        {label}
      </label>
      <div className="flex min-h-9 items-center rounded-md border border-gray-200 bg-gray-50 px-3 py-1.5 text-sm text-gray-700">
        {value || value === 0 ? value : <span className="text-gray-400">—</span>}
      </div>
    </div>
  );

  const renderSection = (title, Icon, children) => (
    <div className="overflow-hidden rounded-xl border border-gray-200 shadow-sm">
      <div className="flex items-center gap-2 border-b border-gray-200 bg-gray-50 px-6 py-4">
        <Icon size={16} className="text-blue-600" />
        <h3 className="text-sm font-medium text-gray-700">{title}</h3>
      </div>
      {children}
    </div>
  );

  const handleDisplayReviewFields = (statusLabel) => {
    const showReviewerFields =
      orderStatus === "approved" ||
      orderStatus === "rejected" ||
      orderStatus === "completed";

    return (
      <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
        {renderField(
          t("purchaseOrder.applicant") || "申請人",
          handleDisplayApplicant(),
          IconUser
        )}
        {renderField(
          t("purchaseOrder.applicationDate") || "申請時間",
          handleDisplayDate(applicationDate, pendingSubmitText),
          IconCalendar
        )}
        {showReviewerFields
          ? renderField(
              t("purchaseOrder.reviewer") || "審核人",
              handleDisplayEmployeeName(reviewerId) || pendingReviewText,
              IconUser
            )
          : null}
        {showReviewerFields
          ? renderField(
              t("purchaseOrder.reviewDate") || "審核時間",
              handleDisplayDate(reviewDate, pendingReviewText),
              IconCalendar
            )
          : null}
        {renderField(
          t("purchaseOrder.reviewStatus") || "審核狀態",
          statusLabel,
          IconCircleCheck
        )}
      </div>
    );
  };

  const handleDisplayDeactivation = (orderStatus) => {
    if (orderStatus === "draft") {
      return renderSection(
        t("purchaseOrder.reviewApplication") || "審核申請",
        IconClipboardCheck,
        <>
          {handleDisplayReviewFields(t("purchaseOrder.draft") || "草稿")}
          <div className="flex justify-center border-t border-gray-100 px-6 py-4">
            <Button
              variant="filled"
              color="gray"
              style={{ minWidth: "250px" }}
              radius="md"
              loading={submitting}
              onClick={handleRequestApproval}
            >
              {t("purchaseOrder.submit") || "送出審核"}
            </Button>
          </div>
        </>
      );
    } else if (orderStatus === "underReview") {
      return renderSection(
        t("purchaseOrder.reviewApplication") || "審核申請",
        IconClipboardCheck,
        <>
          {handleDisplayReviewFields(
            t("purchaseOrder.underReview") || "審核中"
          )}
          {canApprove ? (
            <div className="space-y-4 border-t border-gray-100 px-6 py-4">
              <div className="flex justify-center gap-4">
                <Button
                  variant="filled"
                  color="gray"
                  style={{ minWidth: "250px" }}
                  radius="md"
                  disabled={submitting}
                  onClick={() => setIsRejected(true)}
                >
                  {t("purchaseOrder.rejectAction") || "審核不通過"}
                </Button>
                <Button
                  variant="filled"
                  color="gray"
                  style={{ minWidth: "250px" }}
                  radius="md"
                  loading={submitting}
                  onClick={handleApprove}
                >
                  {t("purchaseOrder.approve") || "審核通過"}
                </Button>
              </div>
              {isRejected ? (
                <div className="space-y-4">
                  <div className="space-y-1.5">
                    <label className="flex items-center gap-1.5 text-xs text-gray-500">
                      <IconFileText size={13} />
                      {t("purchaseOrder.rejectReason") || "審核不通過原因"}
                      <span className="text-red-500">＊</span>
                    </label>
                    <Textarea
                      value={rejectReason}
                      onChange={(e) => setRejectReason(e.target.value)}
                    />
                  </div>
                  <div className="flex justify-center">
                    <Button
                      variant="filled"
                      color="gray"
                      style={{ minWidth: "250px" }}
                      radius="md"
                      loading={submitting}
                      onClick={handleReject}
                    >
                      {t("purchaseOrder.submitReject") || "送出"}
                    </Button>
                  </div>
                </div>
              ) : null}
            </div>
          ) : (
            <div
              className="border-t border-gray-100 px-6 py-4 text-center text-sm"
              style={{ color: "#6F6F6F" }}
            >
              {t("purchaseOrder.noApprovePermission") ||
                "您沒有採購審核權限，請聯絡採購主管"}
            </div>
          )}
        </>
      );
    } else if (orderStatus === "rejected") {
      return renderSection(
        t("purchaseOrder.reviewNotPassed") || "審核未通過",
        IconClipboardCheck,
        <>
          {handleDisplayReviewFields(
            t("purchaseOrder.reviewNotPassed") || "審核未通過"
          )}
          <div className="px-6 pb-6">
            {renderField(
              t("purchaseOrder.rejectReason") || "審核不通過原因",
              rejectedReason || "",
              IconFileText
            )}
          </div>
          <div className="flex justify-center border-t border-gray-100 px-6 py-4">
            <Button
              variant="filled"
              color="gray"
              style={{ minWidth: "250px" }}
              radius="md"
              loading={submitting}
              onClick={handleRequestApproval}
            >
              {t("purchaseOrder.resubmit") || "重新送審"}
            </Button>
          </div>
        </>
      );
    } else if (orderStatus == "approved") {
      return renderSection(
        t("purchaseOrder.reviewPassed") || "已通過",
        IconClipboardCheck,
        <>
          {handleDisplayReviewFields(t("purchaseOrder.reviewPassed") || "已通過")}
          <div className="flex justify-center border-t border-gray-100 px-6 py-4">
            <Button
              variant="filled"
              color="gray"
              style={{ minWidth: "250px" }}
              radius="md"
              loading={submitting}
              onClick={handleComplete}
            >
              {t("purchaseOrder.complete") || "完成"}
            </Button>
          </div>
        </>
      );
    } else if (orderStatus == "completed") {
      return renderSection(
        t("purchaseOrder.completed") || "已完成",
        IconClipboardCheck,
        handleDisplayReviewFields(t("purchaseOrder.completed") || "已完成")
      );
    }
  };

  useEffect(() => {
    fetchOrder();
  }, []);

  useEffect(() => {
    const fetchUserRoles = async () => {
      try {
        const result = await authClient.organization.getActiveMember();
        if (result?.data?.role) {
          setUserRoles([
            ...new Set<string>(
              (result.data.role as string)
                .split(",")
                .map((r: string) => r.trim())
                .filter(Boolean)
            ),
          ]);
        }
      } catch (error) {
        console.error("Error fetching user roles:", error);
      }
    };

    fetchUserRoles();
  }, []);

  useEffect(() => {
    const fetchCurrentEmployee = async () => {
      try {
        const sessionData = await authClient.getSession();
        const employeeId = (sessionData?.data as any)?.employeeId;
        if (employeeId) setCurrentEmployeeId(employeeId);
      } catch (error) {
        console.error("Error fetching current employee:", error);
      }
    };

    fetchCurrentEmployee();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.company.read({});
        setCompanyData(result);
      } catch (error) {
        console.error("Error fetching company data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.employee.list({});
        setEmployeeData(result);
      } catch (error) {
        console.error("Error fetching employee data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    const fetchData = async () => {
      try {
        let result = await orpcClient.partner.list({});
        setPartnerData(result);
      } catch (error) {
        console.error("Error fetching partner data:", error);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    async function fetchProducts() {
      if (!purchaseOrderProduct || !id) return;
      const fetchedData = await Promise.all(
        purchaseOrderProduct.map(async (t) => {
          const result = await single(
            orpcClient.product.read([{ id: t?.productId }])
          );
          return {
            id: result?.id || "N/A",
            name: result?.name || "N/A",
            amount: t?.amount,
            unitPrice: t?.unitPrice,
            unitWeight: result?.unitWeight,
            length: result?.length,
          };
        })
      );

      setProductData(fetchedData);
    }

    fetchProducts();
  }, [purchaseOrderProduct, id]);

  const printData: PurchaseOrderPrintData = {
    companyName: companyData?.name,
    companySubName: companyData?.companyAbbreviation,
    name,
    purchaseDate,
    deliveryDate,
    partnerName: selectedPartner?.name,
    partnerContact: selectedPartner?.contacts?.[0]?.name,
    partnerTelephone: selectedPartner?.telephone,
    partnerFax: selectedPartner?.fax,
    paymentMethod,
    products: productData,
    remark,
    notice,
    applicantName: handleDisplayEmployeeName(applicantId),
    reviewerName: handleDisplayEmployeeName(reviewerId),
  };

  return (
    <div className="min-h-screen p-8">
      <div className="mx-auto max-w-4xl rounded-xl bg-white p-8 space-y-5">
        <div className="flex items-center justify-between">
          <div>
            <div className="flex items-center gap-3">
              <div className="text-4xl font-bold" style={{ color: "#6F6F6F" }}>
                {name || t("purchaseOrder.purchaseOrderName") || "採購單名稱"}
              </div>
              {handleDisplayStatusBadge(orderStatus)}
            </div>
            <div className="mt-2 text-sm text-gray-500">
              {t("purchaseOrder.view") || "查看採購單"}
            </div>
          </div>
          <div className="flex items-center gap-2">
            {canPrint ? (
              <button
                type="button"
                onClick={handlePrint}
                title={t("purchaseOrder.print") || "列印"}
                className="cursor-pointer flex h-10 items-center justify-center gap-2 rounded-lg border border-gray-200 bg-white px-4 text-sm text-gray-600 shadow-sm hover:bg-gray-50"
              >
                <IconPrinter size={18} />
                {t("purchaseOrder.print") || "列印"}
              </button>
            ) : null}
            {orderStatus === "draft" || orderStatus === "rejected" ? (
              <button
                type="button"
                onClick={() => navigate(`/hrMainPage/editPurchaseOrder/${id}`)}
                className="cursor-pointer flex h-10 w-10 items-center justify-center rounded-lg border border-gray-200 bg-white text-gray-600 shadow-sm hover:bg-gray-50"
              >
                <IconPencil size={18} />
              </button>
            ) : null}
          </div>
        </div>

        {renderSection(
          t("purchaseOrder.basicInfo") || "基本資訊",
          IconShoppingCart,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(
              t("purchaseOrder.purchaseOrderName") || "採購單名稱",
              name,
              IconShoppingCart
            )}
            {renderField(
              t("purchaseOrder.purchaseDate") || "採購日期",
              handleDisplayDate(purchaseDate, ""),
              IconCalendar
            )}
            {renderField(
              t("purchaseOrder.partner") || "合作方",
              selectedPartner?.name || "",
              IconBuildingStore
            )}
            {renderField(
              t("purchaseOrder.paymentMethod") || "付款方式",
              paymentMethod,
              IconCreditCard
            )}
            {renderField(
              t("purchaseOrder.shippingFeeCalculation") || "運費計算方式",
              shippingFeeCalculation === "freeShipping"
                ? t("purchaseOrder.freeShipping") || "免運費"
                : t("purchaseOrder.shippingFeeRequired") || "加付運費",
              IconTruck
            )}
            {renderField(
              t("purchaseOrder.actualShippingFee") || "實際運費",
              actualShippingFee,
              IconCash
            )}
            {renderField(
              t("purchaseOrder.orderReminderDate") || "設定下單提醒",
              handleDisplayDate(orderReminderDate, ""),
              IconCalendar
            )}
            {renderField(
              t("purchaseOrder.requiresInvoice") || "是否開發票",
              requiresInvoice
                ? t("purchaseOrder.yes") || "是"
                : t("purchaseOrder.no") || "不是",
              IconReceipt
            )}
            {renderField(
              t("purchaseOrder.requiresIncomingQualityControl") ||
                "是否要進料檢",
              requiresIncomingQualityControl
                ? t("purchaseOrder.yes") || "是"
                : t("purchaseOrder.no") || "不是",
              IconClipboardCheck
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.incomingQCItems") || "進料檢項目",
          IconPaperclip,
          <div className="p-6">
            {handleDisplayAttachment(attachment).length ? (
              <div className="overflow-hidden rounded-lg border border-gray-200">
                {handleDisplayAttachment(attachment).map((file, index) => (
                  <div
                    key={file?.id || file || index}
                    className="flex items-center justify-between gap-4 border-b border-gray-100 px-4 py-2 last:border-b-0"
                  >
                    <div className="flex items-center gap-2 text-sm text-gray-700">
                      <IconFileText size={15} className="text-gray-400" />
                      {file?.name || file}
                    </div>
                    {file?.url ? (
                      <a
                        href={file.url}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="rounded-lg bg-blue-500 px-3 py-1 text-xs text-white transition-all hover:bg-blue-600"
                      >
                        Open
                      </a>
                    ) : null}
                  </div>
                ))}
              </div>
            ) : (
              <div className="text-center text-sm text-gray-400">—</div>
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.product") || "商品",
          IconPackage,
          <div className="space-y-4 p-6">
            {productData.length ? (
              productData.map((product) => (
                <div
                  key={product.id}
                  className="grid grid-cols-1 gap-4 rounded-lg border border-gray-200 p-4 md:grid-cols-[2fr_1fr_1fr_1fr]"
                >
                  {renderField(
                    t("purchaseOrder.productName") || "商品名稱",
                    product?.name,
                    IconPackage
                  )}
                  {renderField(
                    t("purchaseOrder.quantity") || "數量",
                    product.amount,
                    IconHash
                  )}
                  {renderField(
                    t("purchaseOrder.unitPrice") || "單價",
                    product.unitPrice == null
                      ? null
                      : Number(product.unitPrice).toLocaleString(),
                    IconCash
                  )}
                  {renderField(
                    t("purchaseOrder.subtotal") || "小計",
                    product.unitPrice == null || product.amount == null
                      ? null
                      : (
                          Number(product.unitPrice) * Number(product.amount)
                        ).toLocaleString(),
                    IconCash
                  )}
                </div>
              ))
            ) : (
              <div className="text-center text-sm text-gray-400">—</div>
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.deliverySettings") || "交貨設定",
          IconTruck,
          <div className="grid grid-cols-1 gap-4 p-6 md:grid-cols-2 lg:grid-cols-3">
            {renderField(
              t("purchaseOrder.deliveryMethodTitle") || "交貨方式",
              deliveryMethod === "single"
                ? t("purchaseOrder.singleDelivery") || "一次到貨"
                : t("purchaseOrder.partialDelivery") || "分批出貨",
              IconTruck
            )}
            {renderField(
              t("purchaseOrder.deliveryDate") || "交貨日期",
              handleDisplayDate(deliveryDate, ""),
              IconCalendar
            )}
            {renderField(
              t("purchaseOrder.deliveryReceiver") || "交貨簽收人員",
              deliveryReceiver,
              IconUser
            )}
          </div>
        )}

        {renderSection(
          t("purchaseOrder.deliveryLocationTitle") || "交貨地點",
          IconMapPin,
          <div className="p-6">
            {renderField(
              t("purchaseOrder.deliveryLocationTitle") || "交貨地點",
              formatDeliveryLocation(deliveryLocation),
              IconMapPin
            )}
          </div>
          // 後端 deliveryLocation 已由 FullAddress jsonb 改為 text，以下拆欄顯示先保留註解
          // <div className="grid grid-cols-1 gap-4 p-6 sm:grid-cols-2 lg:grid-cols-4">
          //   {renderField(t("purchaseOrder.city"), deliveryLocation?.city, IconMapPin)}
          //   {renderField(t("purchaseOrder.district"), deliveryLocation?.district, IconMapPin)}
          //   {renderField(t("purchaseOrder.postalCode"), deliveryLocation?.postalCode, IconHash)}
          //   {renderField(t("purchaseOrder.address"), deliveryLocation?.address, IconHome)}
          // </div>
        )}

        {renderSection(
          t("purchaseOrder.remarkNoticeTitle") || "備註與注意事項",
          IconNotes,
          <div className="grid grid-cols-1 gap-4 p-6">
            {renderField(
              t("purchaseOrder.remark") || "備註",
              remark ? (
                <span className="whitespace-pre-wrap">{remark}</span>
              ) : null,
              IconNotes
            )}
            {renderField(
              t("purchaseOrder.notice") || "注意事項",
              notice ? (
                <span className="whitespace-pre-wrap">{notice}</span>
              ) : null,
              IconAlertTriangle
            )}
          </div>
        )}

        {handleDisplayDeactivation(orderStatus)}
      </div>

      {canPrint ? <PurchaseOrderPrintSheet data={printData} /> : null}

      <SignPrintPreview
        open={printHtml != null}
        title={name || t("purchaseOrder.printTitle") || "採購單"}
        html={printHtml ?? ""}
        margin={PRINT_PREVIEW_MARGIN}
        onClose={() => setPrintHtml(null)}
      />
    </div>
  );
}
export default PurchaseOrderPage;
